import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { DatePipe } from '@angular/common';
import {
  IGeoJSONPoint,
  IGrupo,
  IFilter,
  IQueryParam,
  IReporteGenerico,
  TipoValoresReporte,
  ILogEvento,
  IResumenDatos,
  IResumenDatosBase,
  NivelDimerizacion,
  IPuntoCurvaLuminaria,
} from 'modelos/src';
import {
  MapaFacilData,
  MarkerMapa,
} from '../../../../../../auxiliares/mapa-facil/interfaces';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../../auxiliares/servicios/lista2.service';
import { LuminariasService } from '../../../service';
import { ChartColors } from '../../../../../../auxiliares/servicios/chart-colors.service';
import { ChartCardConfig } from '../../../../../../componentes/chart-card/chart-card.component';
import { computeCurvaLuminariaSeries } from '../../../../../standalone/modelos-dispositivos/curva-luminaria-chart/curva-luminaria-chart.component';
import { SeriesOptionsType } from 'highcharts';
import { MapCardConfig } from '../../../../../../componentes/map-card/map-card.component';
import { ILuminariaExtendida } from '../../../luminarias.types';
import { ResumenesDatosService } from '../../../../../../auxiliares/servicios/http/resumenes-datos.service';
import { IColumnas } from '../../../../../../auxiliares/tabla/tabla.component';
import { DateRangeValue } from '../../../../../../componentes/date-picker/date-picker.component';
import { TonoEstado } from '../../../../../../componentes/detalle/detalle.components';
import {
  duracion,
  haceCuanto,
} from '../../../../../../auxiliares/estado-conexion.util';
import {
  alarmasActivas,
  calcularEstado,
  CodigoEstadoLuminaria,
  fechaUltimaComunicacion,
  frecuenciaReporteMin,
} from '../../../estado/estado-luminaria';

// Info del voltaje nominal + márgenes del modelo (mostrado en la sección
// de especificaciones eléctricas del modelo de luminaria).
interface VoltajeModeloInfo {
  nominalV: number | null;
  margenSuperiorV: number | null;
  margenInferiorV: number | null;
}

// Grupo de condiciones que disparan una alarma, por categoría. Se arma con los
// umbrales reales de ESTA luminaria (driver del modelo + config del dispositivo)
// para explicarle al usuario cuándo se generan los eventos (popover del card
// "Eventos por tipo de alarma"). Fuente de verdad: reglas-luminaria.ts (backend).
interface CondicionAlarma {
  categoria: string;
  icon: string;
  color: string;
  lineas: string[];
}

// Tipado para filas de encendido diario individual (tabla)
interface EncendidoDetallado {
  _id: string;
  fecha: Date;
  tiempoTotalHoras: number;
  tiempoPromedioHoras: number;
  totalDispositivos: number;
}

/** Una medición eléctrica: el valor actual y, si hay curva, el nominal. */
interface MedicionElectrica {
  actual?: string;
  nominal?: string;
}

/** Lo que muestra la pestaña Info, ya armado para la plantilla. */
interface VistaLuminaria {
  estado: { tono: TonoEstado; etiqueta: string; alarmas: string[] };
  comunicacion: { tono: TonoEstado; etiqueta: string };
  ultimaComunicacion?: string;
  hace?: string;
  /** null: sin reportes en las últimas 24 h. */
  horas24?: string | null;
  consumo24?: string | null;
  // Luminaria
  marcaModelo?: string;
  horasVida?: string;
  horasTotales?: string;
  grupos?: string;
  /** El perfil que se aplica (propio o heredado) y de qué nivel viene. */
  perfil?: string;
  perfilOrigen?: string;
  puesta?: string;
  estadoOperativo?: string;
  fechaCreacion?: string;
  // Eléctrico
  dimerizacion?: string;
  potencia: MedicionElectrica;
  factorPotencia: MedicionElectrica;
  voltaje: MedicionElectrica;
  // Comunicación
  frecuencia?: string;
  senal?: string;
  rssi?: string;
  snr?: string;
  tasaEntrega?: string;
  tiempoLimite?: string;
  // Dispositivo LoRaWAN
  lora: {
    nombre?: string;
    deveui?: string;
    marcaModelo?: string;
    fechaCreacion?: string;
    frecuenciaUplink?: string;
    dataRate?: string;
  };
}

/** Estado de la luminaria (mismo cálculo que el mapa y el listado) a tono. */
const TONO_ESTADO: Record<CodigoEstadoLuminaria, TonoEstado> = {
  0: 'neutro', // Sin nodo
  1: 'apagado', // Error de comunicación
  2: 'alerta', // Error de encendido
  3: 'advertencia', // Alarma
  4: 'ok', // Encendida
  5: 'neutro', // Apagada
};

/** De qué nivel viene el perfil efectivo (mismos nombres que el listado). */
const ORIGEN_PERFIL: Record<string, string> = {
  luminaria: 'propio de la luminaria',
  grupo: 'del grupo de luminarias',
  puesta: 'de la puesta',
  grupoPuesta: 'del grupo de puestas',
};

/** Color de calcularEstadoComunicacion (el del punto del listado) a tono. */
const TONO_COMUNICACION: Record<string, TonoEstado> = {
  green: 'ok',
  yellow: 'advertencia',
  red: 'apagado',
};

@Component({
  selector: 'app-detalles-luminaria-info',
  templateUrl: './detalles-luminaria-info.component.html',
  styleUrl: './detalles-luminaria-info.component.scss',
  standalone: false,
})
export class DetallesLuminariaInfoComponent
  implements OnInit, OnChanges, OnDestroy
{
  helper = inject(HelperService);
  private luminariasService = inject(LuminariasService);
  private lista2 = inject(Lista2Service);
  private resumenesDatosService = inject(ResumenesDatosService);

  @Input() luminaria: ILuminariaExtendida;

  // Ref estable para el gráfico de eventos (evita re-disparar el effect del
  // componente hijo en cada ciclo de detección de cambios).
  public idsEventos: string[] = [];
  private _idEventos?: string;

  public vista?: VistaLuminaria;
  /** El getById no lo trae: lo resuelve el listado (incluirPerfilEfectivo). */
  private perfilEfectivo?: ILuminariaExtendida['perfilEfectivo'];
  /** Horas encendida y consumo de las últimas 24 h (de los reportes). */
  private datos24h: { horas: string | null; consumo: string | null } = {
    horas: null,
    consumo: null,
  };
  public chartCardConfigConsumoMensual: ChartCardConfig;
  public tituloConsumoMensual = 'Consumo mensual';
  public mapCardConfig?: MapCardConfig;

  /**
   * Alto del mapa en px: el de su lugar en la grilla (las dos primeras
   * tarjetas). map-card le resta su encabezado al alto que recibe: con "100%"
   * lo restaría dos veces. Que Mapbox se redimensione lo resuelve map-card.
   */
  public pantallaAncha = signal(window.innerWidth > 1100);
  public altoMapa = signal<number | null>(null);
  private observadorMapa?: ResizeObserver;

  @ViewChild('tarjetaMapa') set tarjetaMapa(
    ref: ElementRef<HTMLElement> | undefined,
  ) {
    this.observadorMapa?.disconnect();
    if (!ref) return;
    this.observadorMapa = new ResizeObserver(([entrada]) => {
      this.altoMapa.set(Math.round(entrada.contentRect.height) || null);
    });
    this.observadorMapa.observe(ref.nativeElement);
  }

  @HostListener('window:resize')
  public alRedimensionar(): void {
    this.pantallaAncha.set(window.innerWidth > 1100);
  }

  // Especificaciones eléctricas del modelo de luminaria (mismos gráficos que
  // en crear/editar modelo: banda de tolerancia potencia y FP vs dimerización).
  public tieneModelo = false;
  public tieneEspecificaciones = false;
  public dimerModelo?: Partial<Record<NivelDimerizacion, IPuntoCurvaLuminaria>>;
  public voltajeModelo: VoltajeModeloInfo | null = null;

  // Opción A: condiciones eléctricas concretas de ESTA luminaria (caption bajo
  // "Especificaciones del modelo"). Solo fallas eléctricas (voltaje/pot/FP).
  public condicionesAlarmaElectricas: string[] = [];
  // Opción B: todas las categorías (eléctrica + encendido + comunicación + GPE)
  // para el popover del card de eventos.
  public condicionesAlarma: CondicionAlarma[] = [];
  public hoy = new Date();
  public rangoMesesConsumo: DateRangeValue = (() => {
    const hoy = new Date();
    const inicio = new Date(hoy.getFullYear(), hoy.getMonth() - 2, 1);
    const fin = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    return { start: inicio.toISOString(), end: fin.toISOString() };
  })();
  public ultimoUplink?: ILogEvento;
  public reportes24hs: IReporteGenerico[] = [];
  public reportesEnergia24hs: IReporteGenerico[] = [];

  // Datos detallados de encendido diario (una fila por día)
  public encendidoDetallado: EncendidoDetallado[] = [];
  public totalCountEncendido = 0;
  public nameEncendido = 'DetallesLuminariaInfoEncendido';
  public queryEncendido: IQueryParam = {
    page: 0,
    limit: 10,
    sort: '-periodoInicio',
  };
  public columnasEncendidoDetallado: IColumnas<EncendidoDetallado>[] = [
    {
      header: { label: 'Fecha', sort: 'fecha' },
      row: { field: 'fecha', pipe: { pipe: DatePipe, args: 'dd/MM/yyyy' } },
    },
    {
      header: {
        label: 'Horas encendidas total',
        sort: 'tiempoTotalHoras',
      },
      row: {
        parse: (dato: EncendidoDetallado) => {
          const valor = Math.round(dato.tiempoTotalHoras * 100) / 100;
          return valor.toFixed(2).replace('.', ',');
        },
      },
    },
    {
      header: {
        label: 'Horas encendidas promedio',
        sort: 'tiempoPromedioHoras',
      },
      row: {
        parse: (dato: EncendidoDetallado) => {
          const valor = Math.round(dato.tiempoPromedioHoras * 100) / 100;
          return valor.toFixed(2).replace('.', ',');
        },
      },
    },
    {
      header: { label: 'Dispositivos', sort: 'totalDispositivos' },
      row: { field: 'totalDispositivos' },
    },
  ];

  constructor() {
    this.queryEncendido.limit = this.helper.getPageSize(this.nameEncendido);
  }

  // Listardos
  private async listarUltimoUplink(): Promise<void> {
    try {
      const filter: IFilter<ILogEvento> = {
        deveui: this.luminaria?.dispositivo?.deveui,
      };
      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        sort: '-fechaCreacion',
        select: 'metadatosUplink',
      };
      const data = await firstValueFrom(this.lista2.logEventos(query));
      this.ultimoUplink = data.datos[0];
    } catch (error) {
      console.error('Error obteniendo último uplink:', error);
    }
  }
  private async listarReportes24h(): Promise<void> {
    try {
      const ahora = new Date();
      const hace24h = new Date(ahora.getTime() - 24 * 60 * 60 * 1000);
      const filter: IFilter<IReporteGenerico> = {
        idsAsignados: this.luminaria?._id,
        'valores.timestamp': {
          $gte: hace24h.toISOString(),
          $lte: ahora.toISOString(),
        },
      } as any;
      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        sort: '-valores.timestamp',
        limit: 500,
        includeChildren: true,
      };
      const tipoEntidad: TipoValoresReporte = this.mapTipoToValores(
        this.luminaria?.dispositivo?.tipo,
      );
      // Usar reportes genéricos via Lista2Service para ambos tipos
      const data = await firstValueFrom(
        this.lista2.reportesGenericos([tipoEntidad], query),
      );
      this.reportes24hs = data.datos;
    } catch (error) {
      console.error('Error obteniendo reportes de las últimas 24h:', error);
    }
  }

  private async listarReportesEnergia24h(): Promise<void> {
    try {
      const ahora = new Date();
      const hace24h = new Date(ahora.getTime() - 24 * 60 * 60 * 1000);
      const filter: IFilter<IReporteGenerico> = {
        idsAsignados: this.luminaria?._id,
        'valores.timestamp': {
          $gte: hace24h.toISOString(),
          $lte: ahora.toISOString(),
        },
      } as any;
      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        sort: '-valores.timestamp',
        limit: 500,
        includeChildren: true,
      };
      const tipoEntidad: TipoValoresReporte = 'Luminaria ACTIS FING Energía';
      const data = await firstValueFrom(
        this.lista2.reportesGenericos([tipoEntidad], query),
      );
      this.reportesEnergia24hs = data.datos;
    } catch (error) {
      console.error(
        'Error obteniendo reportes de energía de las últimas 24h:',
        error,
      );
    }
  }

  public async listarEncendidoDiario(): Promise<void> {
    try {
      if (!this.luminaria?.dispositivo?._id) {
        console.warn(
          'No hay dispositivo asociado para obtener encendido diario',
        );
        this.encendidoDetallado = [];
        this.totalCountEncendido = 0;
        return;
      }

      const filterResumen: IFilter<IResumenDatos> = {
        tipo: 'Encendido Diario Luminarias',
        agrupacion: 'Individual',
        agrupacionTiempo: 'Diario',
        idsAsignados: this.luminaria.dispositivo._id,
      };

      const queryResumen: IQueryParam = {
        filter: JSON.stringify(filterResumen),
        sort: this.queryEncendido.sort ?? '-periodoInicio',
        page: this.queryEncendido.page ?? 0,
        limit: this.queryEncendido.limit ?? 10,
        includeChildren: true,
      };

      const respuesta =
        await this.resumenesDatosService.getFiltered<'Encendido Diario Luminarias'>(
          queryResumen,
        );

      this.totalCountEncendido = respuesta.totalCount ?? 0;
      this.procesarDatosEncendidoDiario(respuesta.datos);
    } catch (error) {
      console.error('Error obteniendo encendido diario:', error);
      this.encendidoDetallado = [];
      this.totalCountEncendido = 0;
    }
  }

  private procesarDatosEncendidoDiario(
    resumenDiario: IResumenDatosBase<'Encendido Diario Luminarias'>[],
  ): void {
    if (!resumenDiario || resumenDiario.length === 0) {
      this.encendidoDetallado = [];
      return;
    }

    const filas: EncendidoDetallado[] = resumenDiario.map((item) => {
      const fechaBase = new Date(
        item.periodoInicio ?? item.fechaCreacion ?? new Date().toISOString(),
      );

      const totalHoras = item.resumen?.tiempoEncendidoTotal ?? 0;
      const promedioHoras = item.resumen?.tiempoEncendidoPromedio ?? 0;
      const dispositivos = item.resumen?.totalDispositivos ?? 0;

      return {
        _id: item._id,
        fecha: fechaBase,
        tiempoTotalHoras: Math.round(totalHoras * 100) / 100,
        tiempoPromedioHoras: Math.round(promedioHoras * 100) / 100,
        totalDispositivos: dispositivos,
      } as EncendidoDetallado;
    });

    this.encendidoDetallado = filas;
  }

  private mapTipoToValores(tipo?: string): TipoValoresReporte {
    const mapa: Record<string, TipoValoresReporte> = {
      'Luminaria GPE': 'Luminaria GPE Periódico',
      'Luminaria ACTIS FING': 'Luminaria ACTIS FING Estado',
      'Luminaria Wellness': 'Luminaria Wellness',
    };
    return (tipo && mapa[tipo]) || 'Luminaria Wellness';
  }

  /**
   * Arma la vista con lo que haya llegado. Se llama al cambiar la luminaria y
   * cuando llegan el último uplink y los reportes de 24 h, que terminan en
   * cualquier orden.
   */
  private armarVista(): void {
    const l = this.luminaria;
    if (!l) return;

    // Estado y comunicación con el cálculo del módulo de luminarias (el mismo
    // del mapa y el listado), no con el de trackers.
    const estado = calcularEstado(l);
    const com = this.luminariasService.calcularEstadoComunicacion(l);
    const fechaMs = fechaUltimaComunicacion(l);
    const fecha = fechaMs != null ? new Date(fechaMs).toISOString() : null;
    const frec = frecuenciaReporteMin(l);

    // Valores actuales y nominales (curva del modelo en el nivel de
    // dimerización vigente).
    const driver = l.modeloDispositivo?.luminarias;
    const periodico = l.ultimoReportePeriodico?.valores as
      | Record<string, unknown>
      | undefined;
    const energia = l.ultimoReporteEnergia?.valores as
      | Record<string, unknown>
      | undefined;
    const dimming = this.numOrNull(periodico?.['dimmingValue']);
    // Con la luminaria apagada (0 %) no hay nominal: getNivelDimerizacion
    // redondea al 10 % y mostraba el valor de la curva en ese nivel.
    const nivel =
      dimming != null && dimming > 0
        ? this.getNivelDimerizacion(dimming)
        : null;
    const punto =
      nivel && driver?.dimerizacion
        ? (
            driver.dimerizacion as Record<
              string,
              { potencia?: number; factorPotencia?: number }
            >
          )[nivel]
        : undefined;
    const medicion = (
      actual: number | null,
      nominal: number | null | undefined,
      unidad: string,
    ): MedicionElectrica => {
      const sufijo = unidad ? ` ${unidad}` : '';
      return {
        actual:
          actual != null ? `${this.fmtNum(actual, 2)}${sufijo}` : undefined,
        nominal:
          nominal != null && isFinite(nominal)
            ? `${this.fmtNum(nominal, 2)}${sufijo}`
            : undefined,
      };
    };

    // Señal del último uplink: el mejor de los gateways que lo recibieron.
    const rx = this.ultimoUplink?.metadatosUplink?.rxInfo ?? [];
    const rssi = rx.length
      ? Math.max(...rx.map((r) => r.rssi ?? -Infinity))
      : null;
    const snr = rx.length
      ? Math.max(...rx.map((r) => r.snr ?? -Infinity))
      : null;
    const perdida = l.dispositivo?.paquetes?.perdidaPaquetes;
    const tasa =
      typeof perdida === 'number' && isFinite(perdida)
        ? Math.max(0, Math.min(100, 100 - perdida))
        : null;
    const calidad =
      tasa == null
        ? ''
        : tasa >= 95
          ? 'excelente'
          : tasa >= 80
            ? 'aceptable'
            : 'crítica';
    const frecHz = this.ultimoUplink?.metadatosUplink?.txInfo?.frequency;
    const marca = l.modeloDispositivo?.marca;
    const modelo = l.modeloDispositivo?.modelo;
    const marcaLora = l.dispositivo?.modeloDispositivo?.marca;
    const modeloLora = l.dispositivo?.modeloDispositivo?.modelo;
    const tiempoLimite = l.dispositivo?.tiempoLimiteComunicacion;

    this.vista = {
      estado: {
        tono: TONO_ESTADO[estado.codigo],
        etiqueta: estado.label,
        alarmas: estado.codigo === 3 ? alarmasActivas(l) : [],
      },
      comunicacion: {
        tono: TONO_COMUNICACION[com.color] ?? 'neutro',
        etiqueta: com.tooltip,
      },
      ultimaComunicacion: com.fecha || undefined,
      hace: fecha ? haceCuanto(fecha) : undefined,
      horas24: this.datos24h.horas,
      consumo24: this.datos24h.consumo,
      marcaModelo: [marca, modelo].filter(Boolean).join(' · ') || undefined,
      horasVida: driver?.horasVida
        ? `${this.fmtNum(driver.horasVida, 0)} h`
        : undefined,
      horasTotales:
        l.tiempoEncendida != null
          ? `${this.fmtNum(Math.floor(l.tiempoEncendida), 0)} h`
          : undefined,
      grupos: this.getNombresGrupos(l.grupos) || undefined,
      // El efectivo (el que se aplica), no solo el propio: una luminaria sin
      // perfil propio puede heredarlo de su grupo o puesta.
      perfil:
        this.perfilEfectivo?.nombre ?? l.perfilConfig?.nombre ?? undefined,
      perfilOrigen: this.perfilEfectivo
        ? ORIGEN_PERFIL[this.perfilEfectivo.nivel]
        : undefined,
      puesta: l.puesta?.identificacion,
      estadoOperativo: l.estadoOperativo,
      fechaCreacion: l.fechaCreacion
        ? this.helper.formatearFecha(l.fechaCreacion)
        : undefined,
      dimerizacion: dimming != null ? `${dimming} %` : undefined,
      potencia: medicion(
        this.numOrNull(energia?.['potencia']),
        punto?.potencia,
        'W',
      ),
      factorPotencia: medicion(
        this.numOrNull(energia?.['factorPotencia']),
        punto?.factorPotencia,
        '',
      ),
      voltaje: medicion(
        this.numOrNull(energia?.['voltaje']),
        driver?.voltaje?.nominalV,
        'V',
      ),
      // Sin frecuencia configurada (Wellness, o GPE/ACTIS sin config) se dice;
      // antes se mostraba un valor por defecto que no era el del equipo.
      frecuencia: frec ? duracion(frec) : 'No configurada',
      senal:
        l.dispositivo?.margin != null
          ? `${l.dispositivo.margin} dB`
          : undefined,
      rssi:
        rssi != null && isFinite(rssi)
          ? `${this.fmtNum(rssi, 0)} dBm`
          : undefined,
      snr:
        snr != null && isFinite(snr) ? `${this.fmtNum(snr, 1)} dB` : undefined,
      tasaEntrega:
        tasa != null ? `${this.fmtNum(tasa, 1)} % (${calidad})` : undefined,
      tiempoLimite: tiempoLimite ? `${tiempoLimite} h` : undefined,
      lora: {
        nombre: l.dispositivo?.name,
        deveui: l.dispositivo?.deveui,
        marcaModelo:
          [marcaLora, modeloLora].filter(Boolean).join(' · ') || undefined,
        fechaCreacion: l.dispositivo?.fechaCreacion
          ? this.helper.formatearFecha(l.dispositivo.fechaCreacion)
          : undefined,
        frecuenciaUplink: frecHz
          ? `${this.fmtNum(frecHz / 1_000_000, 3)} MHz`
          : undefined,
        dataRate:
          this.ultimoUplink?.metadatosUplink?.dr != null
            ? String(this.ultimoUplink.metadatosUplink.dr)
            : undefined,
      },
    };
  }

  /**
   * Perfil efectivo (propio o heredado de grupo o puesta). Solo el listado lo
   * resuelve, así que se pide filtrado por esta luminaria y con lo mínimo.
   */
  private async listarPerfilEfectivo(): Promise<void> {
    const id = this.luminaria?._id;
    if (!id) return;
    try {
      const query = {
        filter: JSON.stringify({ _id: id }),
        select: 'idsGrupos idPuesta idPerfilConfig',
        includeChildren: true,
        incluirPerfilEfectivo: true,
      } as IQueryParam;
      const data = await firstValueFrom(this.lista2.luminarias(query));
      this.perfilEfectivo = data.datos?.[0]?.perfilEfectivo ?? null;
      this.armarVista();
    } catch (error) {
      console.error('Error obteniendo el perfil efectivo:', error);
    }
  }

  /**
   * Horas encendida y consumo de las últimas 24 h. Sin reportes en ese lapso
   * queda en null ("Sin información"); antes se mostraba 0, como si la
   * luminaria hubiera estado apagada.
   */
  private async calcularDatos24h(): Promise<void> {
    let horas: string | null = null;
    let consumo: string | null = null;
    try {
      await this.listarReportes24h();
      if (this.reportes24hs.length > 0) {
        horas = `${this.fmtNum(this.calcularHorasEncendidas24h(this.reportes24hs), 1)} h`;
        // ACTIS reporta la energía en reportes aparte
        let reportesEnergia = this.reportes24hs;
        if (this.luminaria?.dispositivo?.tipo === 'Luminaria ACTIS FING') {
          await this.listarReportesEnergia24h();
          reportesEnergia = this.reportesEnergia24hs;
        }
        if (reportesEnergia.length > 0) {
          const wh = this.calcularConsumo24h(reportesEnergia);
          consumo =
            wh >= 1000
              ? `${this.fmtNum(wh / 1000, 2)} kWh`
              : `${this.fmtNum(wh, 2)} Wh`;
        }
      }
    } catch (error) {
      console.error('Error obteniendo reportes 24h:', error);
    }
    this.datos24h = { horas, consumo };
    this.armarVista();
  }

  // Snap dimmingValue numérico (0-100) al nivel 'dim10'..'dim100'.
  private getNivelDimerizacion(dimmingValue: number): string {
    const snap = Math.max(
      10,
      Math.min(100, Math.round(dimmingValue / 10) * 10),
    );
    return `dim${snap}`;
  }

  private fmtNum(n: number, decimales: number): string {
    return n.toLocaleString('es-AR', {
      minimumFractionDigits: decimales,
      maximumFractionDigits: decimales,
    });
  }

  private numOrNull(v: unknown): number | null {
    return typeof v === 'number' && isFinite(v) ? v : null;
  }

  private calcularHorasEncendidas24h(reportes: IReporteGenerico[]): number {
    if (!reportes || reportes.length === 0) return 0;

    // Usar el acumulado de horas encendidas: valores.tiempoEncendida
    const validos = reportes
      .filter((r) => r?.valores && (r.valores as any)?.timestamp)
      .sort((a, b) => {
        const ta = new Date((a.valores as any).timestamp).getTime();
        const tb = new Date((b.valores as any).timestamp).getTime();
        return ta - tb;
      });

    if (validos.length < 2) return 0;

    const primero = validos[0];
    const ultimo = validos[validos.length - 1];

    const t0 = Number((primero.valores as any)?.tiempoEncendida);
    const t1 = Number((ultimo.valores as any)?.tiempoEncendida);

    if (!isFinite(t0) || !isFinite(t1)) return 0;

    const diff = t1 - t0;
    return diff > 0 ? diff : 0;
  }
  private calcularConsumo24h(reportes: IReporteGenerico[]): number {
    if (!reportes || reportes.length === 0) return 0;

    // Filtrar solo reportes válidos con potencia
    // Los reportes ya vienen filtrados por timestamp y ordenados de más reciente a más antiguo
    const validos = reportes.filter((r) => {
      if (!r?.valores) return false;
      const valores = r.valores as any;
      return typeof valores?.potencia === 'number';
    });

    if (validos.length === 0) return 0;

    // Invertir para tener orden cronológico (más antiguo a más reciente)
    validos.reverse();

    let consumoTotal = 0;

    // Para cada par de reportes consecutivos, calcular el consumo
    for (let i = 0; i < validos.length - 1; i++) {
      const reporteActual = validos[i];
      const reporteSiguiente = validos[i + 1];

      const valoresActual = reporteActual.valores as any;
      const valoresSiguiente = reporteSiguiente.valores as any;

      const timestampActual = new Date(valoresActual.timestamp).getTime();
      const timestampSiguiente = new Date(valoresSiguiente.timestamp).getTime();

      // Calcular tiempo transcurrido en horas
      const tiempoHoras =
        (timestampSiguiente - timestampActual) / (1000 * 60 * 60);

      // Usar la potencia del reporte actual para ese período
      const potencia = valoresActual.potencia as number;

      // Calcular consumo en Wh (potencia en W * tiempo en horas)
      const consumoParcial = potencia * tiempoHoras;
      consumoTotal += consumoParcial;
    }

    return consumoTotal;
  }

  // Especificaciones eléctricas del modelo (potencia / FP / voltaje).
  // Los gráficos los renderiza <app-curva-luminaria-chart> (mismo componente
  // que crear/editar modelo); acá sólo resolvemos qué mostrar.
  private construirEspecificacionesModelo(): void {
    const modelo = this.luminaria?.modeloDispositivo;
    this.tieneModelo = !!(modelo?.marca || modelo?.modelo);

    const driver = modelo?.luminarias;
    this.dimerModelo = driver?.dimerizacion;
    const voltaje = driver?.voltaje;

    this.voltajeModelo =
      voltaje &&
      (voltaje.nominalV != null ||
        voltaje.margenSuperiorV != null ||
        voltaje.margenInferiorV != null)
        ? {
            nominalV: voltaje.nominalV ?? null,
            margenSuperiorV: voltaje.margenSuperiorV ?? null,
            margenInferiorV: voltaje.margenInferiorV ?? null,
          }
        : null;

    const { hayCurva } = computeCurvaLuminariaSeries(this.dimerModelo);
    this.tieneEspecificaciones = hayCurva || !!this.voltajeModelo;
  }

  // Condiciones que disparan cada evento, con los umbrales REALES de esta
  // luminaria. Debe reflejar reglas-luminaria.ts del backend; si cambian las
  // fórmulas allá, actualizar acá. Sirve para el caption (opción A) y el
  // popover del card de eventos (opción B).
  private construirCondicionesAlarma(): void {
    // --- Fallas eléctricas (concretas, con números) ---
    const electricas: string[] = [];

    // Voltaje: el backend usa 220 V ± 40 V por defecto si el modelo no lo define.
    const usaDefaultVoltaje = !this.voltajeModelo;
    const vNom = this.voltajeModelo?.nominalV ?? 220;
    const vSup = this.voltajeModelo?.margenSuperiorV ?? 40;
    const vInf = this.voltajeModelo?.margenInferiorV ?? 40;
    const notaDef = usaDefaultVoltaje
      ? ' (valores por defecto: el modelo no define voltaje)'
      : '';
    electricas.push(
      `Sobretensión: voltaje ≥ ${vNom + vSup} V (nominal ${vNom} + ${vSup})${notaDef}`,
    );
    electricas.push(
      `Subtensión: voltaje ≤ ${vNom - vInf} V (nominal ${vNom} − ${vInf})${notaDef}`,
    );

    // Potencia y FP: la referencia es el punto de curva del nivel de dimerización
    // vigente; solo se evalúan con la luminaria encendida (dimming > 0).
    const potSup = this.resumirMargenCurva((p) => p?.margenPotenciaSuperior);
    const potInf = this.resumirMargenCurva((p) => p?.margenPotenciaInferior);
    if (potSup != null || potInf != null) {
      electricas.push(
        this.describirBanda(
          'Potencia',
          potSup,
          potInf,
          '%',
          'de la potencia de la curva',
        ),
      );
    }
    const fpSup = this.resumirMargenCurva(
      (p) => p?.margenFactorPotenciaSuperior,
    );
    const fpInf = this.resumirMargenCurva(
      (p) => p?.margenFactorPotenciaInferior,
    );
    if (fpSup != null || fpInf != null) {
      electricas.push(
        this.describirBanda(
          'Factor de potencia',
          fpSup,
          fpInf,
          '',
          'del FP de la curva',
        ),
      );
    }

    this.condicionesAlarmaElectricas = electricas;

    // --- Todas las categorías para el popover (opción B) ---
    const tipo = this.luminaria?.dispositivo?.tipo;
    const tiempoLimite = this.luminaria?.dispositivo?.tiempoLimiteComunicacion;
    const cats: CondicionAlarma[] = [];

    cats.push({
      categoria: 'Falla eléctrica',
      icon: 'bolt',
      color: '#f5a623',
      lineas: electricas,
    });

    cats.push({
      categoria: 'Error de encendido',
      icon: 'error',
      color: '#e53935',
      lineas: [
        'Encendida de día: está encendida fuera del horario nocturno esperado (amanecer/atardecer astronómico).',
        'Apagada de noche: está apagada dentro del horario nocturno esperado.',
        'En modo manual se registra como informativo, con tolerancia de ±30 min.',
      ],
    });

    cats.push({
      categoria: 'Error de comunicación',
      icon: 'cloud_off',
      color: '#fb8c00',
      lineas: [
        // El default del alta de dispositivo es 24 h; si nunca se configuró,
        // el cron NO genera el evento (requiere tiempoLimite > 0).
        tiempoLimite != null && tiempoLimite > 0
          ? `Sin comunicación: no reporta hace más de ${tiempoLimite} h.`
          : 'Sin comunicación: no reporta hace más de 24 h (valor por defecto).',
      ],
    });

    if (tipo === 'Luminaria GPE') {
      cats.push({
        categoria: 'Fallas de energía (GPE)',
        icon: 'power_off',
        color: '#8e24aa',
        lineas: [
          'Falla de alimentación externa: se perdió la energía de red.',
          'Error de lectura / parámetros de energía: hay energía pero las mediciones son inconsistentes.',
          'Relé encendido sin consumo / apagado con consumo: el estado del relé no coincide con la potencia medida.',
        ],
      });
    }

    this.condicionesAlarma = cats;
  }

  // Resume un margen de la curva de dimerización a un único valor. Devuelve el
  // número si es igual en todos los niveles cargados, NaN si varía por nivel
  // (→ "según nivel"), o null si no hay ninguno definido.
  private resumirMargenCurva(
    extraer: (p: IPuntoCurvaLuminaria) => number | null | undefined,
  ): number | null {
    const curva = this.dimerModelo;
    if (!curva) return null;
    const valores = (Object.keys(curva) as NivelDimerizacion[])
      .map((k) => extraer(curva[k] as IPuntoCurvaLuminaria))
      .filter((v): v is number => typeof v === 'number' && isFinite(v));
    if (!valores.length) return null;
    const distintos = Array.from(new Set(valores));
    return distintos.length === 1 ? distintos[0] : NaN;
  }

  // Describe la condición de una banda de tolerancia (potencia / FP). Los
  // márgenes viven por nivel de dimerización y pueden ser distintos en cada uno
  // (cargados a mano o según una función), así que solo se muestra un número
  // cuando es igual en todos los niveles; si varían, se remite al gráfico de
  // tolerancia que ya se renderiza en esta card.
  private describirBanda(
    etiqueta: string,
    sup: number | null,
    inf: number | null,
    unidad: string,
    refCurva: string,
  ): string {
    const base = `${etiqueta}: alarma cuando la medición sale de la banda de tolerancia ${refCurva} del nivel de dimerización actual`;
    const varia = (v: number | null) => v != null && isNaN(v);
    if (varia(sup) || varia(inf)) {
      return `${base}; los márgenes varían por nivel (ver gráfico de tolerancia arriba)`;
    }
    const s = sup != null ? `+${sup}${unidad}` : 'sin información';
    const i = inf != null ? `−${inf}${unidad}` : 'sin información';
    return `${base} (márgenes del modelo ${s} / ${i})`;
  }

  // Map Card
  public generarMapCard(): void {
    const coords = (this.luminaria?.ubicacion as IGeoJSONPoint)?.coordinates;
    if (!coords?.[0]) {
      this.mapCardConfig = undefined;
      return;
    }
    // Solo la primera vez: con un refresco de la luminaria no se le pisa el
    // zoom ni el encuadre al usuario.
    if (this.mapCardConfig) return;
    // Mapbox usa [lon, lat] directamente, no requiere conversión
    const marker: MarkerMapa = {
      label: 'puntoSimple', // Marker genérico tipo gota
      coor: coords as [number, number],
      centrar: true,
    };
    const data: MapaFacilData = {
      markers: [marker],
      center: coords as [number, number],
      zoom: 17,
    };
    // La dirección ya es el subtítulo del encabezado de la página.
    this.mapCardConfig = {
      title: 'Ubicación',
      icon: 'location_on',
      color: 'red',
      mapOptions: data,
      nombreMap: 'MapCardLuminaria', // Usar nombre distinto para que use pin fijo
    };
  }

  public async onRangoMesesChange(rango: DateRangeValue): Promise<void> {
    this.rangoMesesConsumo = rango;
    await this.generarChartConsumoMensual();
  }

  private getMesesEnRango(start: Date, end: Date): Date[] {
    const meses: Date[] = [];
    const current = new Date(start.getFullYear(), start.getMonth(), 1);
    const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
    while (current <= endMonth) {
      meses.push(new Date(current));
      current.setMonth(current.getMonth() + 1);
    }
    return meses;
  }

  private async generarChartConsumoMensual(): Promise<void> {
    try {
      const filterResumen: IFilter<IResumenDatos> = {
        tipo: 'Consumo Mensual Luminarias',
        agrupacion: 'Individual',
        agrupacionTiempo: 'Mensual',
        idCliente: this.luminaria?.idCliente,
        idsAsignados: this.luminaria?._id,
      };

      const queryResumen: IQueryParam = {
        filter: JSON.stringify(filterResumen),
        sort: 'periodoInicio',
        includeChildren: true,
      };

      const resumen: IResumenDatosBase<'Consumo Mensual Luminarias'>[] = (
        await this.resumenesDatosService.getFiltered<'Consumo Mensual Luminarias'>(
          queryResumen,
        )
      ).datos;

      const mesesRango = this.getMesesEnRango(
        new Date(this.rangoMesesConsumo.start),
        new Date(this.rangoMesesConsumo.end),
      );

      const ultimos = mesesRango.map((mes) => {
        const encontrado = resumen.find((r) => {
          const d = new Date(r.periodoInicio!);
          return (
            d.getFullYear() === mes.getFullYear() &&
            d.getMonth() === mes.getMonth()
          );
        });
        return (
          encontrado ||
          ({
            periodoInicio: mes.toISOString(),
            resumen: { consumoTotal: 0 },
          } as IResumenDatosBase<'Consumo Mensual Luminarias'>)
        );
      });

      const monthNames = [
        'Ene',
        'Feb',
        'Mar',
        'Abr',
        'May',
        'Jun',
        'Jul',
        'Ago',
        'Sep',
        'Oct',
        'Nov',
        'Dic',
      ];

      const categoriasConsumo = ultimos.map((d) => {
        const fecha = new Date(d.periodoInicio!);
        return `${monthNames[fecha.getMonth()]}/${fecha.getFullYear()}`;
      });

      const datosNumeros = ultimos.map((d) => {
        const v = d.resumen?.consumoTotal ?? 0;
        return Math.round(v * 100) / 100;
      });

      const series: SeriesOptionsType[] = [
        {
          type: 'column',
          name: 'Consumo',
          data: datosNumeros,
          colorByPoint: true,
          colors: [ChartColors.color1()],
        },
      ];

      const chartOptions: Highcharts.Options = {
        chart: { type: 'column' },
        legend: { enabled: false },
        xAxis: {
          categories: categoriasConsumo,
          title: { text: 'Mes/Año' },
        },
        yAxis: {
          title: { text: 'Consumo Total (kWh)' },
        },
        series,
      };

      const startDate = new Date(this.rangoMesesConsumo.start);
      const endDate = new Date(this.rangoMesesConsumo.end);
      const startLabel = `${monthNames[startDate.getMonth()]}/${startDate.getFullYear()}`;
      const endLabel = `${monthNames[endDate.getMonth()]}/${endDate.getFullYear()}`;

      // El título va en la tarjeta (app-dt-card), con el período.
      this.tituloConsumoMensual = `Consumo mensual (${startLabel} - ${endLabel})`;
      this.chartCardConfigConsumoMensual = {
        showShadow: false,
        chartOptions,
      };
    } catch (error) {
      console.error('Error al generar gráfico de consumo mensual:', error);
      this.crearGraficoConsumoVacio();
    }
  }

  private crearGraficoConsumoVacio(): void {
    const monthNames = [
      'Ene',
      'Feb',
      'Mar',
      'Abr',
      'May',
      'Jun',
      'Jul',
      'Ago',
      'Sep',
      'Oct',
      'Nov',
      'Dic',
    ];
    const meses = this.getMesesEnRango(
      new Date(this.rangoMesesConsumo.start),
      new Date(this.rangoMesesConsumo.end),
    );
    const categoriasVacias = meses.map(
      (m) => `${monthNames[m.getMonth()]}/${m.getFullYear()}`,
    );

    const chartOptions: Highcharts.Options = {
      chart: { type: 'column' },
      legend: { enabled: false },
      xAxis: {
        categories: categoriasVacias,
        title: { text: 'Mes/Año' },
      },
      yAxis: {
        title: { text: 'Consumo Total (kWh)' },
      },
      series: [
        {
          type: 'column',
          name: 'Consumo',
          data: meses.map(() => 0),
          colorByPoint: true,
          colors: [ChartColors.color1()],
        },
      ],
    };

    const startDate = new Date(this.rangoMesesConsumo.start);
    const endDate = new Date(this.rangoMesesConsumo.end);
    const startLabel = `${monthNames[startDate.getMonth()]}/${startDate.getFullYear()}`;
    const endLabel = `${monthNames[endDate.getMonth()]}/${endDate.getFullYear()}`;

    // El título va en la tarjeta (app-dt-card), con el período.
    this.tituloConsumoMensual = `Consumo mensual (${startLabel} - ${endLabel})`;
    this.chartCardConfigConsumoMensual = {
      showShadow: false,
      chartOptions,
    };
  }

  private getNombresGrupos(grupos: IGrupo[]): string {
    return grupos?.map((g) => g.nombre).join(', ') ?? '';
  }

  // Mantiene `idsEventos` con la misma referencia mientras no cambie la luminaria.
  private actualizarIdsEventos(): void {
    const id = this.luminaria?._id;
    if (id === this._idEventos) return;
    this._idEventos = id;
    this.idsEventos = id ? [id] : [];
  }

  //Hooks
  ngOnInit() {
    if (!this.luminaria) return;
    this.actualizarIdsEventos();
  }

  ngOnDestroy(): void {
    this.observadorMapa?.disconnect();
  }

  async ngOnChanges() {
    if (!this.luminaria) return;
    this.actualizarIdsEventos();
    this.construirEspecificacionesModelo();
    this.construirCondicionesAlarma();
    this.generarMapCard();
    this.armarVista();
    await Promise.all([
      this.listarUltimoUplink().then(() => this.armarVista()),
      this.listarPerfilEfectivo(),
      this.calcularDatos24h(),
      this.generarChartConsumoMensual(),
      this.listarEncendidoDiario(),
    ]);
  }
}
